import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { v4 as uuidv4 } from 'uuid';

import { ModalContent, ModalBody, ModalHeader, ModalFooter } from '@atoms/new/modal';
import Button from '@atoms/new/button';

import { useStreamData } from '@lib/hooks/useStreamData';

import InteractionQuestion from './AdhocInteraction/InteractionQuestion';
import InteractionSetting from './AdhocInteraction/InteractionSetting';
import InteractionHeader from './AdhocInteraction/InteractionHeader';

import SettingBlock from './Presentation/main/Settings/units/SettingBlock';

import { getDefaultTitle } from '@components/Presentation/helpers';
import { getInteractionSettings } from '@services/utils.service';

import { ThemeCard } from '@components/themes';
import useFetchThemes from '@components/themes/hooks/useFetchThemes';

import {
  getCommentsDetails,
  getFeaturesCommentDetails,
  createRosQuestions,
  getQuestion,
  updateStreamSettings,
  getWheelResults,
  getTemplateTitle,
} from '@services/youtube-platform.service';

import { insertQuestion, defaultPollOptions, defaultWheelOptions, defaultRatingPollsOptions, defaultMultpleChoiceOptions, handleSaveOptions, defaultQuizOptions } from '@services/interactions.service';
import { useThemeStore } from '@components/themes/store/ThemeStore';
import useLoadingTimeout from '@lib/hooks/useLoadingTimeout';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { getMCQWinners } from '@services/presentation-live';
import { hasReachedPlanLimit, usePurchasePlan } from '@lib/hooks/usePlanSettings';
import { usePostHog } from 'posthog-js/react';
import { getAuth } from '@services/identity.service';
import { useRemoveDarkClass } from '@lib/hooks/useRemoveDarkClass';
import { useOffice } from '@components/OfficeLoader';
import { isInIframe } from '@embed/helpers';
import { ArrowLeftIcon as ArrowBackIcon } from '@phosphor-icons/react';

export const ALL_MCQ_SCORERS_ID = '79a4e569-ff0d-47d0-b7d5-af797f8c02c4';

const AdhocInteractionPopup = ({
  templateId,
  themeColors,
  setShowRosModel,
  streamId,
  isEDU,
  isImpromptu,
  adhocFeature,
  featureType,
  featureTitle,
  streamSetting,
  defaultColors,
  linkQuestions,
  setLinkQuestions,
  currentIndex,
  setCurrentIndex,
  newQuestion,
  handleLinkFeature,
  isTrack,
  openModal,
  setOpenModal,
  setImpromptuModel,
  stopInteractionInOtherTabs,
  handleStopFeature,
  currentRosFeature,
  isPptInteractionLink = false,
  hasStream = true,
  isZoomApp = false,
  onPreviewChange,
  setIsImpromtu,
  onCancel,
  onAdhocStartBegin,
  onAdhocStartError,
  onInteractionCreated,
}) => {
  const posthog = usePostHog();
  const { isPowerpoint } = useOffice();
  const router = useRouter();

  const getQuestionTitle = () => {
    if (featureTitle) {
      return featureTitle;
    } else if (isEDU && ['newWheel', 'wheel'].includes(featureType)) {
      return 'Let’s spin a wheel to pick a participant';
    } else if (['newWheel', 'Quick Questions', 'linkLibrary', 'textTrack', 'fantasticFans', 'credits', 'ratingPolls'].includes(featureType)) {
      return getDefaultTitle(featureType);
    } else {
      return '';
    }
  };

  if (featureType === 'quiz') defaultQuizOptions[0].checked = true;

  const defaultOptionsMap = {
    pollWithOptions: defaultPollOptions,
    wheel: defaultWheelOptions,
    ratingPolls: defaultRatingPollsOptions,
    multipleChoice: defaultMultpleChoiceOptions,
    quiz: defaultQuizOptions
  };

  const getOptions = () => {
    if (isPowerpoint || isPptInteractionLink || isInIframe()) {
      return adhocFeature?.options?.length > 0 ? adhocFeature.options : defaultOptionsMap[featureType];
    }
    return isEDU && !isImpromptu ? adhocFeature?.options : defaultOptionsMap[featureType];
  };

  const [selectedPanelBtn, setSelectedPanelBtn] = useState('Content');
  const [title, setTitle] = useState(() => getQuestionTitle());
  const [setting, setSetting] = useState(() => getInteractionSettings(featureType, streamSetting));
  const [color, setColor] = useState(defaultColors);
  const [options, setOptions] = useState(() => getOptions());

  const { defaultThemes, userThemes, defaultThemeId } = useFetchThemes();
  const [selectedThemeId, setSelectedThemeId] = useState(defaultThemeId || '');
  const [, setStore] = useThemeStore(store => store.themeMenu);

  const [colorThemes = [], pictureThemes = [], seasonalThemes = [], motionThemes = []] = useMemo(() => {
    const ct = [];
    const pt = [];
    const st = [];
    const vt = [];
    defaultThemes?.forEach(theme => {
      if (theme.config?.default?.type === 'Seasonal') st.push(theme);
      if (theme.config?.default?.type === 'Motion') vt.push(theme);
      else if (theme.config?.default?.imageSrc) pt.push(theme);
      else ct.push(theme);
    });
    return [ct, pt, st, vt];
  }, [defaultThemes]);

  const allInteractions = {
    id: streamId,
    title: 'Select participants who commented...',
    type: 'All Interactions'
  };

  const multipleChoiceInteraction = {
    id: ALL_MCQ_SCORERS_ID,
    title: 'Select from all multiple choice participants',
    type: 'multipleChoice'
  };

  const [isLoading, setIsLoading] = useLoadingTimeout(30000);
  const [winnerWheelsData, setWinnerWheelData] = useState([allInteractions]);
  const [selectedInteraction, setSelectedInteraction] = useState(allInteractions);
  useRemoveDarkClass();

  const getUsers = async () => {
    const response = await getCommentsDetails(streamId, setting.wheelShowUser);
    if (response.status) {
      return response.entity.length;
    }
  };

  const getInteractionUsers = async (interactionId) => {
    const response = await getFeaturesCommentDetails(streamId, interactionId, setting.wheelShowUser);
    if (response.status) {
      return response.entity.length;
    }
  };

  useEffect(() => {
    if (!userThemes || !defaultThemes) return;
    setStore({ themes: { userThemes, defaultThemes } });
    updateTheme([...userThemes, ...defaultThemes].find(theme => theme.id === defaultThemeId) || {});
  }, [userThemes, defaultThemes, defaultThemeId]);

  useEffect(() => {
    setSelectedThemeId(defaultThemeId);
  }, [defaultThemeId]);

  // Sync preview changes to parent for real-time updates
  useEffect(() => {
    onPreviewChange?.({ title, options, color, setting });
  }, [title, options, color, setting]);

  const { teamContext, currentTeamBillingAccountOwnerID } = useTeamsData((s) => ({ teamContext: s.teamContext, currentTeamBillingAccountOwnerID: s.currentTeamBillingAccountOwnerID }));

  const {
    setFeatureQuestion,
    setFeatureSettings,
    setFeatureBrandColours,
  } = useStreamData(s => ({
    setFeatureQuestion: s.setFeatureQuestion,
    setFeatureSettings: s.setFeatureSettings,
    setFeatureBrandColours: s.setFeatureBrandColours
  }));

  async function getInteractionDetails(winnerWheelsData) {
    const interactionDetails = await Promise.all(
      winnerWheelsData.map(async (interaction) => {
        const totalCommenters = interaction.id === streamId
          ? await getUsers()
          : await getInteractionUsers(interaction.id);
        if (totalCommenters === 0) return null;
        return {
          ...interaction,
          totalCommenters,
        };
      })
    );

    return interactionDetails.filter(Boolean);
  }

  const questionOffset = 1;

  const fetchWheelResults = async () => {
    if (featureType !== 'newWheel') return;
    setIsLoading(true);

    const response = await getWheelResults(streamId);
    if (response.status) {
      const interactionsWithoutMultipleChoice = response.entity.filter(interaction => interaction.type !== 'multipleChoice');
      const interactionDetails = await getInteractionDetails([allInteractions, ...response.entity]);
      let totalMultipleChoiceInteractions = response.entity.length - interactionsWithoutMultipleChoice.length;
      if (totalMultipleChoiceInteractions > 1) {
        const totalMultipleChoiceCommenters = await getMCQWinners(streamId);
        multipleChoiceInteraction.totalCommenters = totalMultipleChoiceCommenters.entity.length || 0;
        interactionDetails.push(multipleChoiceInteraction);
      }
      setSelectedInteraction(interactionDetails.find(interaction => interaction.id === streamId));
      setWinnerWheelData(interactionDetails);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isPptInteractionLink && !hasStream) return;
    fetchWheelResults();
  }, [featureType]);

  const { settings: planSettings } = usePurchasePlan(currentTeamBillingAccountOwnerID);

  const updateTotalCommenters = async () => {
    const interactionDetails = await getInteractionDetails(winnerWheelsData);
    setSelectedInteraction(interactionDetails.find(interaction => interaction.id === selectedInteraction.id));
    setWinnerWheelData(interactionDetails);
  };

  useEffect(() => {
    if (isPptInteractionLink && !hasStream) return;
    updateTotalCommenters();
  }, [setting.wheelShowUser]);

  const addNewOption = () => {
    const nextOptionNumber = options.length + 1;
    const usedBodies = new Set(options.map(o => (o.body || '').trim()));
    let nextLabelNumber = 1;
    while (usedBodies.has(`Option ${nextLabelNumber}`)) {
      nextLabelNumber += 1;
    }
    const body = `Option ${nextLabelNumber}`;

    if (nextOptionNumber <= 10 && featureType === 'pollWithOptions') {
      const option = { key: uuidv4(), body, option_number: nextOptionNumber };
      setOptions([...options, option]);
    }

    if (nextOptionNumber <= 10 && featureType === 'multipleChoice') {
      const option = { key: uuidv4(), body, option_number: nextOptionNumber };
      setOptions([...options, option]);
    }

    if (nextOptionNumber <= 4 && featureType === 'quiz') {
      const option = { key: uuidv4(), body, option_number: nextOptionNumber, checked: false };
      setOptions([...options, option]);
    }
  };

  const deleteOption = async (option) => {
    const nonDeletedOptions = options.filter((opt) => opt !== option);
    const updatedOptions = nonDeletedOptions.map((opt, index) => ({ ...opt, option_number: index + 1 }));

    setOptions(updatedOptions);

    // await deleteRosOptions(option.id);
  };

  const updateSetting = (key, value) => {
    let data;

    switch (key) {
      case 'DisplayStyle':
        data = !value ? 'noOfVotes' : 'percentage';
        break;
      case 'transientThoughtDisplayStyle':
        data = value;
        break;
      case 'OptionsList':
        data = !value ? 'character' : 'number';
        break;
      default:
        data = value;
    }

    setSetting(prevSetting => ({
      ...prevSetting,
      [key]: data
    }));
  };

  const updateBulkOptions = (optionCount) => {
    setOptions(() => {
      const newOptions = Array.from({ length: optionCount }, (_, index) => ({
        key: uuidv4(),
        body: (index + 1).toString(),
        option_number: index + 1
      }));
      return newOptions;
    });
  };

  const updateTheme = (theme) => {
    setSelectedThemeId(theme.id);
    setColor((prevColors) => {
      const colors = { ...theme.config.colors };

      if (!('multipleChoiceImageSrc' in theme.config.images)) {
        colors.multipleChoiceBgColor = theme.config.default.bgColor;
        colors.multipleChoiceHeadingColor = theme.config.default.headingColor;
        colors.multipleChoiceChartColor = '#F97B5F';
        colors.multipleChoiceLabelColor = '#FFFFFF';
      }
      if (!('quizImageSrc' in theme.config.images)) {
        colors.quizBgColor = theme.config.default.bgColor;
        colors.quizHeadingColor = theme.config.default.headingColor;
        colors.quizChartColor = '#F97B5F';
        colors.quizLabelColor = '#FFFFFF';
      }
      if (!('leaderboardImageSrc' in theme.config.images)) {
        colors.leaderboardBgColor = theme.config.default.bgColor;
        colors.leaderboardHeadingColor = theme.config.default.headingColor;
        colors.leaderboardChartColor = '#F97B5F';
        colors.leaderboardLabelColor = '#FFFFFF';
      }

      return { ...prevColors, ...colors };
    });

    setSetting((prevSetting) => {
      const fonts = theme.config.textFontFamily;
      const images = theme.config.images;

      let FontStyle = prevSetting.FontStyle;

      const newSetting = { ...prevSetting, mapConfig: { ...prevSetting.mapConfig, style: theme?.config?.settings?.mapConfig?.style || 'Standard' } };

      const featureTypeSettings = {
        map: () => {
          newSetting.mapImageSrc = images.mapImageSrc;
          newSetting.mapImageOpacity = images.mapImageOpacity;
        },
        freeForm: () => {
          FontStyle = fonts.pollFontStyle;
          newSetting.pollImageSrc = images.pollImageSrc;
          newSetting.pollImageOpacity = images.pollImageOpacity;
        },
        pollWithOptions: () => {
          FontStyle = fonts.pollWithOptionsFontStyle;
          newSetting.pollWithOptionsImageSrc = images.pollWithOptionsImageSrc;
          newSetting.pollWithOptionsImageOpacity = images.pollWithOptionsImageOpacity;
        },
        wordCloud: () => {
          FontStyle = fonts.wordCloudFontStyle;
          newSetting.wcImageSrc = images.wcImageSrc;
          newSetting.wcImageOpacity = images.wcImageOpacity;
        },
        emojisEverywhere: () => {
          newSetting.emojisEverywhereImageSrc = images.emojisEverywhereImageSrc;
          newSetting.emojisEverywhereImageOpacity = images.emojisEverywhereImageOpacity;
        },
        fantasticFans: () => {
          FontStyle = fonts.fantasticFansFontStyle;
          newSetting.ffImageSrc = images.ffImageSrc;
          newSetting.ffImageOpacity = images.ffImageOpacity;
        },
        credits: () => {
          FontStyle = fonts.creditsFontStyle || fonts.fantasticFansFontStyle;
          newSetting.creditsImageSrc = images.creditsImageSrc || images.ffImageSrc;
          newSetting.creditsImageOpacity = images.creditsImageOpacity ?? images.ffImageOpacity;
        },
        textTrack: () => {
          FontStyle = fonts.textTrackFontStyle;
          newSetting.textTrackImageSrc = images.textTrackImageSrc;
          newSetting.textTrackImageOpacity = images.textTrackImageOpacity;
        },
        transientThought: () => {
          FontStyle = fonts.transientThoughtFontStyle;
          newSetting.ttImageSrc = images.ttImageSrc;
          newSetting.ttImageOpacity = images.ttImageOpacity;
        },
        talkingTiles: () => {
          newSetting.talkingTilesImageSrc = images.talkingTilesImageSrc;
          newSetting.talkingTilesImageOpacity = images.talkingTilesImageOpacity;
        },
        wheel: () => {
          newSetting.choiceCircleImageSrc = images.choiceCircleImageSrc;
          newSetting.choiceCircleImageOpacity = images.choiceCircleImageOpacity;
        },
        newWheel: () => {
          newSetting.winningWheelImageSrc = images.winningWheelImageSrc;
          newSetting.winningWheelImageOpacity = images.winningWheelImageOpacity;
        },
        linkLibrary: () => {
          newSetting.linkLibraryImageSrc = images.linkLibraryImageSrc;
          newSetting.linkLibraryImageOpacity = images.linkLibraryImageOpacity;
        },
        'Quick Questions': () => {
          newSetting.quickQuestionImageSrc = images.quickQuestionImageSrc;
          newSetting.quickQuestionImageOpacity = images.quickQuestionImageOpacity;
        },
        ratingPolls: () => {
          newSetting.ratingPollsImageSrc = images.ratingPollsImageSrc;
          newSetting.ratingPollsImageOpacity = images.ratingPollsImageOpacity;
        },
        multipleChoice: () => {
          FontStyle = fonts.multipleChoiceFontStyle || 'Sans-Serif';
          if (!('multipleChoiceImageSrc' in theme.config.images)) {
            newSetting.HeadingFont = theme.config.default.headingFontFamily;
            newSetting.multipleChoiceImageSrc = theme.config.default.imageSrc;
            newSetting.multipleChoiceImageOpacity = theme.config.default.imageOpacity;
          } else {
            newSetting.HeadingFont = theme.config.default.headingFontFamily;
            newSetting.multipleChoiceImageSrc = images.multipleChoiceImageSrc;
            newSetting.multipleChoiceImageOpacity = images.multipleChoiceImageOpacity;
          }
        },
        quiz: () => {
          FontStyle = fonts.quizFontStyle || 'Sans-Serif';
          if (!('quizImageSrc' in theme.config.images)) {
            newSetting.HeadingFont = theme.config.default.headingFontFamily;
            newSetting.quizImageSrc = theme.config.default.imageSrc;
            newSetting.quizImageOpacity = theme.config.default.imageOpacity;
          } else {
            newSetting.HeadingFont = theme.config.default.headingFontFamily;
            newSetting.quizImageSrc = images.quizImageSrc;
            newSetting.quizImageOpacity = images.quizImageOpacity;
          }
        }
      };

      if (featureTypeSettings[featureType]) {
        featureTypeSettings[featureType]();
      }

      return { ...newSetting, FontStyle };
    });
  };

  const setSelectedQuestions = (question) => {
    if (isTrack) {
      handleLinkFeature(question);
    } else {
      setFeatureQuestion(question);
    }
  };

  const handleLinkQuestions = (response, isUserTemplate, createdQuestionResponse) => {
    if (isPptInteractionLink) return;
    let updatedQuestions = insertQuestion(
      [...linkQuestions],
      { ...createdQuestionResponse, isAdhoc: true },
      currentIndex + questionOffset
    );
    setLinkQuestions(updatedQuestions);
    setCurrentIndex(Math.max(0, currentIndex + questionOffset + (isEDU ? -1 : 0)));
  };

  const handleAddTrackInteractionToROS = async (createQuestion) => {
    const response = await createRosQuestions({ ...createQuestion });

    if (response.status) {
      const { entity: { createdQuestionResponse, isUserTemplate } } = response;
      const questionId = createdQuestionResponse.id;

      setSelectedQuestions(createdQuestionResponse);

      handleLinkQuestions(response, isUserTemplate, createdQuestionResponse);
      redirectToFeature(questionId);

      setShowRosModel(false);
    }
  };

  const redirectToFeature = async (questionId) => {
    // Embed connect pages handle the post-create transition themselves (stay on
    // the page, swap ?type for ?slideId) instead of redirecting to /connect-to-stream.
    if (onInteractionCreated) {
      onInteractionCreated(questionId);
      return;
    }

    const { userDetailsId, isOnboarding } = router.query;

    if (!isPptInteractionLink) {
      if (openModal === 'default') {
        setOpenModal('showMenu');
      }

      router.replace({
        pathname: `/streams/${streamId}/ros`,
        query: { userDetailsId, isOnboarding },
      });
      return;
    }

    if (isPptInteractionLink && hasStream && streamId) {
      if (openModal === 'default') {
        setOpenModal('showMenu');
      }
      const interactionLink = `/streams/${streamId}/ros?selectedSlideId=${questionId}&userDetailsId=${userDetailsId}&isOnboarding=${isOnboarding}&isEDU=true`;
      router.replace(interactionLink);
      return;
    }

    const interactionLink = `/connect-to-stream?templateId=${templateId}&selectedSlideId=${questionId}&userDetailsId=${userDetailsId}&isOnboarding=${isOnboarding}&isEDU=true`;
    router.replace(interactionLink);
  };

  const handleStartAdhocInteraction = async () => {
    onAdhocStartBegin?.();
    if (!isPptInteractionLink) {
      stopInteractionInOtherTabs();
      handleStopFeature();
    }

    const trackInteractions = [
      'Quick Questions',
      'linkLibrary',
      'textTrack',
      'fantasticFans',
      'credits'
    ];

    let newSetting = featureType === 'map' ? { ...setting, mapConfig: { style: 'Standard' } } : setting;

    const createQuestion = {
      title,
      live_stream_id: streamId,
      userId: teamContext?.oid,
      type: featureType,
      questionNumber: isPptInteractionLink ? null : currentIndex + questionOffset,
      setting: newSetting,
      color,
      metadata: {}
    };

    if (isPptInteractionLink && !hasStream) {
      createQuestion.templateId = templateId;
      createQuestion.isEDU = true;
    }

    if (featureType === 'newWheel') {
      createQuestion.metadata.featureId = selectedInteraction?.id;
      createQuestion.metadata.featureType = selectedInteraction?.type;
    }

    setFeatureSettings(newSetting);
    setFeatureBrandColours(color);

    if (trackInteractions.includes(featureType)) {
      await handleAddTrackInteractionToROS(createQuestion);
      return;
    }

    posthog?.capture('Adhoc Interaction Started', {
      streamId,
      featureType,
      title,
    });

    if (newQuestion) {
      const response = await createRosQuestions({ ...createQuestion });
      if (!response.status) {
        onAdhocStartError?.();
        return;
      }
      if (response.status) {
        const { entity: { createdQuestionResponse, isUserTemplate } } = response;
        const questionId = createdQuestionResponse.id;

        const featureWithOptions = ['pollWithOptions', 'wheel', 'multipleChoice', 'ratingPolls', 'quiz', 'newWheel'];

        if (featureWithOptions.includes(featureType)) {
          await handleSaveOptions(featureType, options, questionId);
        }

        const fetchQuestion = await getQuestion(questionId);

        if (fetchQuestion.status) {
          const fetchedQuestion = fetchQuestion.entity;
          setSelectedQuestions(fetchedQuestion);
          createdQuestionResponse.options = fetchQuestion.entity.options;
        } else {
          const defaultQuestion = {
            id: questionId,
            type: featureType,
            title,
            setting,
            color,
            options,
            metadata: {}
          };
          if (featureType === 'newWheel') {
            defaultQuestion.metadata.featureId = selectedInteraction.id;
            defaultQuestion.metadata.featureType = selectedInteraction.type;
          }
          setSelectedQuestions(defaultQuestion);
          createdQuestionResponse.options = options;
        }

        if (!isPptInteractionLink) {
          await updateStreamSettings(streamId, { settings: setting });
        }

        handleLinkQuestions(response, isUserTemplate, createdQuestionResponse);
        redirectToFeature(questionId);
      }
    }
  };

  const hasSingleOption = options?.length > 0 && options.filter(option => option.body.trim()).length <= 1;

  const auth = getAuth();

  async function getTemplateId() {
    let templateId;
    linkQuestions.forEach((question) => {
      if (question.template_id) {
        templateId = question.template_id;
        return true;
      }
      return false;
    });

    if (!templateId) return;

    const res = await getTemplateTitle(templateId, auth, teamContext?.oid, { lightweight: true });

    let tempId = res?.entity?.selected_theme_id || defaultThemeId;

    setSelectedThemeId(tempId);
    const filteredTheme = [...userThemes || [], ...defaultThemes || []].filter(theme => theme.id === tempId) || {};

    if (filteredTheme.length > 0) {
      updateTheme(filteredTheme[0]);
    }
  }
  useEffect(() => {
    if (isPptInteractionLink && !hasStream) return;
    getTemplateId();
  }, [userThemes, defaultThemes]);

  return (
    <ModalContent className="text-default-900">
      <ModalHeader>
        <InteractionHeader
          featureType={featureType}
          selectedPanelBtn={selectedPanelBtn}
          setSelectedPanelBtn={setSelectedPanelBtn}
        />
      </ModalHeader>
      <ModalBody className="pt-0">
        {(selectedPanelBtn === 'Content') && (
          <>
            <div className="flex flex-col">
              <InteractionQuestion
                featureType={featureType}
                title={title}
                setTitle={setTitle}
                options={options}
                setOptions={setOptions}
                addNewOption={addNewOption}
                deleteOption={deleteOption}
                setting={setting}
                updateSetting={updateSetting}
              />
              <InteractionSetting
                featureType={featureType}
                setting={setting}
                options={options}
                setOptions={(value) => {
                  setOptions(value);
                }}
                updateSetting={updateSetting}
                winnerWheelsData={winnerWheelsData}
                selectedInteraction={selectedInteraction}
                updateBulkOptions={updateBulkOptions}
                setSelectedInteraction={setSelectedInteraction}
              />
            </div>
          </>
        )}
        {selectedPanelBtn === 'Themes' && (
          <div className="flex flex-col">
            {userThemes?.length > 0 && <SettingBlock title="Your themes" addPadding={false} defaultExpanded={true} hasBorderTop={false}>
              <div className='grid grid-cols-3 gap-4'>
                {userThemes?.map((theme, index) => <ThemeCard
                  height='h-[107px]'
                  key={theme.id}
                  theme={theme}
                  isDefault={theme.id === defaultThemeId}
                  isSelected={theme.id === selectedThemeId}
                  showMenu={false}
                  limitExceeded={hasReachedPlanLimit(userThemes?.length || 0, planSettings?.customThemesLimit)}
                  isDisabled={hasReachedPlanLimit(index, planSettings?.customThemesLimit)}
                  onClick={() => {
                    updateTheme(theme);
                  }}
                />)}
              </div>
            </SettingBlock>}
            <SettingBlock title="Classic themes" addPadding={false} defaultExpanded={true}>
              <div className='grid grid-cols-3 gap-4'>
                {colorThemes?.map(theme => <ThemeCard
                  height='h-[107px]'
                  key={theme.id}
                  theme={theme}
                  isStandardTheme
                  isDefault={defaultThemeId ? theme.id === defaultThemeId : theme.name === 'Dark'}
                  isSelected={theme.id === selectedThemeId}
                  showMenu={false}
                  onClick={() => {
                    updateTheme(theme);
                  }}
                />)}
              </div>
            </SettingBlock>
            {!!motionThemes?.length && <SettingBlock title="Motion themes" addPadding={false} defaultExpanded={false}>
              <div className='grid grid-cols-3 gap-4'>
                {motionThemes?.map(theme => <ThemeCard
                  height='h-[107px]'
                  key={theme.id}
                  theme={theme}
                  isStandardTheme
                  isDefault={defaultThemeId ? theme.id === defaultThemeId : theme.name === 'Dark'}
                  isSelected={theme.id === selectedThemeId}
                  showMenu={false}
                  onClick={() => {
                    updateTheme(theme);
                  }}
                />)}
              </div>
            </SettingBlock>}
            <SettingBlock title="Scenic themes" addPadding={false} defaultExpanded={false}>
              <div className='grid grid-cols-3 gap-4'>
                {pictureThemes?.map(theme => <ThemeCard
                  height='h-[107px]'
                  key={theme.id}
                  theme={theme}
                  isStandardTheme
                  isDefault={defaultThemeId ? theme.id === defaultThemeId : theme.name === 'Dark'}
                  isSelected={theme.id === selectedThemeId}
                  showMenu={false}
                  onClick={() => {
                    updateTheme(theme);
                  }}
                />)}
              </div>
            </SettingBlock>
            {!!seasonalThemes?.length && <SettingBlock title="Seasonal themes" addPadding={false} defaultExpanded={false}>
              <div className='grid grid-cols-3 gap-4'>
                {seasonalThemes?.map(theme => <ThemeCard
                  height='h-[107px]'
                  key={theme.id}
                  theme={theme}
                  isStandardTheme
                  isDefault={defaultThemeId ? theme.id === defaultThemeId : theme.name === 'Dark'}
                  isSelected={theme.id === selectedThemeId}
                  showMenu={false}
                  onClick={() => {
                    updateTheme(theme);
                  }}
                />)}
              </div>
            </SettingBlock>}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="w-full flex justify-between">
          <div>
            {!isEDU && (
              <Button
                kind="tertiary"
                startContent={<ArrowBackIcon size={24} />}
                onClick={() => {
                  setShowRosModel(false);
                  setImpromptuModel(true);
                }} >
                Back
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            {!isZoomApp && <Button
              kind="secondary"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                  return;
                }
                if (isPowerpoint || isInIframe()) {
                  router.back();
                  return;
                }
                if (isEDU) {
                  router.push('/interaction-links');
                }
                setShowRosModel(false);
              }}
            >
              Cancel
            </Button>}
            <Button
              disabled={((featureType === 'wheel') && hasSingleOption) || (featureType === 'newWheel' && selectedInteraction?.totalCommenters === 0 && selectedInteraction.type !== 'All Interactions')}
              tooltipMessage={(featureType === 'newWheel' && selectedInteraction?.totalCommenters === 0 && selectedInteraction.type !== 'All Interactions') && 'There haven\'t been enough commenters to run this interaction.'}
              onClick={handleStartAdhocInteraction}
            >
              Go
            </Button>
          </div>
        </div>
      </ModalFooter>
    </ModalContent>
  );
};

export default AdhocInteractionPopup;
